import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  listDeletionRequests,
  deleteAccountData,
  markDeletionProcessed,
  restoreDeletionRequest,
  type DeletionRequestRow,
  type DeletionBlocker,
} from "@/lib/account-deletion.functions";
import { isDeleteConfirmValid, isStaleRequest, waitingDays, DELETE_CONFIRM_WORD } from "@/lib/account-deletion";

export const Route = createFileRoute("/_authenticated/admin/deletions")({
  head: () => ({
    meta: [
      { title: "Запросы на удаление — админка TurkeyDrive" },
      { name: "description", content: "Обработка запросов участников на удаление аккаунта." },
      { property: "og:title", content: "Запросы на удаление — админка TurkeyDrive" },
      { property: "og:description", content: "Обработка запросов участников на удаление аккаунта." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: AdminDeletionsPage,
});

const fmt = (d: string | null) =>
  d ? new Date(d).toLocaleDateString("ru-RU", { day: "numeric", month: "long", year: "numeric" }) : "—";

const ACTION_LABEL = { deleted: "Данные удалены", marked: "Отмечено обработанным", restored: "Профиль возвращён" };

function AdminDeletionsPage() {
  const list = useServerFn(listDeletionRequests);
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({ queryKey: ["admin", "deletions"], queryFn: () => list() });
  const [toDelete, setToDelete] = useState<DeletionRequestRow | null>(null);
  const [toRestore, setToRestore] = useState<DeletionRequestRow | null>(null);
  const [showProcessed, setShowProcessed] = useState(false);
  const mark = useServerFn(markDeletionProcessed);
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["admin", "deletions"] });
    void qc.invalidateQueries({ queryKey: ["admin", "deletions-count"] });
  };

  if (error) {
    return <div className="p-6 text-center text-sm text-td-muted">Эта страница только для администраторов.</div>;
  }

  return (
    <div className="mx-auto max-w-3xl px-4 py-6">
      <Link to="/profile" className="text-[13px] text-td-muted hover:text-td-dark">← В профиль</Link>
      <h1 className="mb-1 mt-3 text-2xl font-semibold text-td-dark">Запросы на удаление</h1>
      <p className="mb-5 text-sm text-td-muted">Самые старые сверху. Ничего не удаляется автоматически.</p>

      {isLoading && <p className="text-sm text-td-muted">Загружаем…</p>}
      {data && data.pending.length === 0 && (
        <p className="rounded-2xl border border-td-line bg-white p-4 text-sm text-td-muted">Необработанных запросов нет.</p>
      )}

      <div className="space-y-3">
        {data?.pending.map((r) => {
          const days = waitingDays(r.requested_at);
          const stale = isStaleRequest(r.requested_at);
          const ev = r.blockers.filter((b) => b.kind === "event").length;
          const ci = r.blockers.filter((b) => b.kind === "circle").length;
          return (
            <div key={r.user_id} className="rounded-2xl border border-td-line bg-white p-4">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-semibold text-td-dark">{r.full_name || "Без имени"}</div>
                  <div className="break-all text-sm text-td-muted">{r.email ?? "email не найден"}</div>
                </div>
                <span
                  className={`shrink-0 rounded-full px-3 py-1 text-xs font-semibold ${
                    stale ? "bg-destructive/10 text-destructive" : "bg-td-soft text-td-dark"
                  }`}
                >
                  ждёт {days} {days % 10 === 1 && days % 100 !== 11 ? "день" : days % 10 >= 2 && days % 10 <= 4 && (days % 100 < 10 || days % 100 >= 20) ? "дня" : "дней"}
                </span>
              </div>
              <div className="mt-2 grid gap-x-4 gap-y-1 text-sm text-td-dark sm:grid-cols-2">
                <div>Город: {r.city || "—"}</div>
                <div>Регистрация: {fmt(r.registered_at)}</div>
                <div>Запрос: {fmt(r.requested_at)}</div>
                <div>Мероприятий: {ev} · Сообществ: {ci}</div>
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                <Button size="sm" variant="destructive" onClick={() => setToDelete(r)}>Удалить данные</Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={async () => {
                    try {
                      await mark({ data: { userId: r.user_id } });
                      toast.success("Отмечено обработанным");
                      refresh();
                    } catch (e) {
                      toast.error(e instanceof Error ? e.message : "Не получилось");
                    }
                  }}
                >
                  Отметить обработанным
                </Button>
                <Button size="sm" variant="outline" onClick={() => setToRestore(r)}>Вернуть профиль</Button>
              </div>
            </div>
          );
        })}
      </div>

      {data && data.processed.length > 0 && (
        <div className="mt-8">
          <button type="button" onClick={() => setShowProcessed((s) => !s)} className="text-sm font-semibold text-td-teal">
            {showProcessed ? "▾" : "▸"} Обработанные ({data.processed.length})
          </button>
          {showProcessed && (
            <ul className="mt-3 divide-y divide-td-line rounded-2xl border border-td-line bg-white">
              {data.processed.map((l) => (
                <li key={l.id} className="px-4 py-3 text-sm">
                  <div className="text-td-dark">{l.full_name || "Без имени"} <span className="break-all text-td-muted">· {l.email ?? "—"}</span></div>
                  <div className="text-xs text-td-muted">
                    {ACTION_LABEL[l.action]} · {fmt(l.processed_at)} · {l.processed_by_name || "админ"}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <DeleteDialog row={toDelete} onClose={() => setToDelete(null)} onDone={refresh} />
      <RestoreDialog row={toRestore} onClose={() => setToRestore(null)} onDone={refresh} />
    </div>
  );
}

function DeleteDialog({ row, onClose, onDone }: { row: DeletionRequestRow | null; onClose: () => void; onDone: () => void }) {
  const del = useServerFn(deleteAccountData);
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [blockers, setBlockers] = useState<DeletionBlocker[] | null>(null);
  const shown = blockers ?? (row?.blockers.length ? row.blockers : null);
  const close = () => {
    setConfirm("");
    setBlockers(null);
    onClose();
  };
  return (
    <Dialog open={!!row} onOpenChange={(o) => !o && close()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Это необратимо</DialogTitle>
          <DialogDescription>
            Аккаунт и персональные данные {row?.full_name || "человека"} будут стёрты навсегда. Перед этим ему уйдёт письмо об удалении.
          </DialogDescription>
        </DialogHeader>
        {shown ? (
          <div className="space-y-2 text-sm text-td-dark">
            <p className="font-semibold">Сначала архивируй или передай:</p>
            <ul className="list-disc pl-5">
              {shown.map((b) => (
                <li key={b.kind + b.id}>{b.kind === "event" ? "Мероприятие" : "Сообщество"}: {b.title}</li>
              ))}
            </ul>
          </div>
        ) : (
          <div className="space-y-2">
            <p className="text-sm text-td-dark">
              Впиши слово <span className="font-semibold">{DELETE_CONFIRM_WORD}</span> или email человека.
            </p>
            <Input value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="off" />
          </div>
        )}
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={close}>Отмена</Button>
          {!shown && (
            <Button
              variant="destructive"
              disabled={busy || !row || !isDeleteConfirmValid(confirm, row.email)}
              onClick={async () => {
                if (!row) return;
                setBusy(true);
                try {
                  const res = await del({ data: { userId: row.user_id, confirm } });
                  if (!res.ok) {
                    setBlockers(res.blockers);
                  } else {
                    toast.success("Аккаунт и данные удалены");
                    onDone();
                    close();
                  }
                } catch (e) {
                  toast.error(e instanceof Error ? e.message : "Не получилось удалить");
                } finally {
                  setBusy(false);
                }
              }}
            >
              {busy ? "Удаляем…" : "Удалить навсегда"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RestoreDialog({ row, onClose, onDone }: { row: DeletionRequestRow | null; onClose: () => void; onDone: () => void }) {
  const restore = useServerFn(restoreDeletionRequest);
  const [busy, setBusy] = useState(false);
  return (
    <Dialog open={!!row} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Вернуть профиль?</DialogTitle>
          <DialogDescription>
            Запрос на удаление снимется, а профиль {row?.full_name || ""} снова появится в разделе «Люди».
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}>Отмена</Button>
          <Button
            disabled={busy}
            onClick={async () => {
              if (!row) return;
              setBusy(true);
              try {
                await restore({ data: { userId: row.user_id } });
                toast.success("Профиль возвращён");
                onDone();
                onClose();
              } catch (e) {
                toast.error(e instanceof Error ? e.message : "Не получилось");
              } finally {
                setBusy(false);
              }
            }}
          >
            Вернуть
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
